import React, { Component } from 'react';
import Loading from '../../components/Loading';

import DataTable from '../../components/Datatable';
import { COLUMNS } from './keys';
import { getCTRAverage } from './query';
import { v4 as uuid } from 'uuid';
import { getSortKeys, setAdditionalStyles } from '../../helpers/table';
import AdditionalBox from './additionalBox';
import moment from 'moment';
import ExtraButtons from './extraBtns';
import { withTranslation } from 'react-i18next';
import { gscTestQuery } from '../GSCTestData/query';

class LiveOptimizationReport extends Component {
    constructor(props) {
        super();
        this.getData = this.getData.bind(this);
        this.showLowCTR = this.showLowCTR.bind(this);
        this.changeAverageCTR = this.changeAverageCTR.bind(this);
        this.calculateLowCtr = this.calculateLowCtr.bind(this);
        this.getAllData = this.getAllData.bind(this);
        this.getCtrAverage = getCTRAverage;

        this.domainId = props.domain_id;
        this.domainUrl = props.domain_url;

        this.state = {
            isLoading: false,
            allData: [],
            filteredData: [],
            isShowLowCTR: false,
            lastUpdatedDate: null,
            averageCTR: 0,
            getCtrLoader: true,
        };
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter((item) =>
            (item.page ?? item.keys?.[0])?.toLowerCase()?.includes(search.toLowerCase()),
        );
    }

    calculateLowCtr(newCtr) {
        const filteredData =
            this.state.allData?.filter((item) => {
                const ctr = item.clicks / item.impressions;
                return ctr < (newCtr ?? this.state.averageCTR) / 100;
            }) ?? [];

        this.setState({
            filteredData,
        });
    }

    changeAverageCTR(value) {
        if (this.state.isShowLowCTR) {
            this.calculateLowCtr(value);
        }
        this.setState({
            averageCTR: value,
        });
    }

    showLowCTR() {
        const newValue = !this.state.isShowLowCTR;
        if (newValue) {
            this.calculateLowCtr();
            this.setState({
                isShowLowCTR: newValue,
            });
        } else {
            this.setState({
                filteredData: this.state.allData,
                isShowLowCTR: newValue,
            });
        }
    }

    async getData() {
        try {
            this.setState({ isLoading: true, allData: [] });
            const today = moment().format('YYYY-MM-DD');
            const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

            const resource = `{
                "startDate": "${yesterday}",
                "endDate": "${today}",
              "dataState": "HOURLY_ALL",
                "dimensions": [
                  "page", "hour"
                ],
                "type": "DISCOVER"
                          }
                          `;

            const params = {
                resource: resource,
                domain: this.domainUrl,
            };

            const body = {
                domain_id: this.domainId,
                queries: [
                    {
                        ...params,
                        key: 'data',
                    },
                ],
                max_count: 10000,
            };

            const response = await gscTestQuery(body);

            const data = await response.json();
            const results = data?.results;

            if (data?.status === 200 && results) {
                if (!results?.data?.length)
                    return this.setState({ error: { ...this.state.error, textarea: 'No Data' } });

                const combinedResults = results?.data.reduce((acc, curr) => {
                    const page = curr.keys[0];
                    acc[page] = {
                        clicks: (acc[page]?.clicks ?? 0) + curr.clicks,
                        ctr: (acc[page]?.ctr ?? 0) + curr.ctr,
                        impressions: (acc[page]?.impressions ?? 0) + curr.impressions,
                        page,
                        clicksL: [
                            ...(acc[page]?.clicksL ?? []),
                            { date: curr.keys[1], value: curr.clicks },
                        ],
                        ctrL: [...(acc[page]?.ctrL ?? []), { date: curr.keys[1], value: curr.ctr }],
                        impressionsL: [
                            ...(acc[page]?.impressionsL ?? []),
                            { date: curr.keys[1], value: curr.impressions },
                        ],
                    };

                    return acc;
                }, {});
                const lastUpdatedDate = results?.data.reduce((acc, curr) => {
                    if (!acc) return curr.keys[1];
                    return new Date(curr.keys[1]).getTime() > new Date(acc).getTime()
                        ? curr.keys[1]
                        : acc;
                }, null);

                const dataAsArray = Object.values(combinedResults);
                const dataWithIds = dataAsArray?.map((item) => ({ ...item, id: uuid() }));
                this.setState({
                    allData: dataWithIds,
                    filteredData: dataWithIds,
                    lastUpdatedDate,
                });
            } else {
                this.setState({
                    allData: [],
                    error: {
                        ...this.state.error,
                        textarea: data?.message ?? 'Something went wrong. Please try again.',
                    },
                });
            }
        } catch (err) {
            return this.setState({
                error: {
                    ...this.state.error,
                    textarea: err?.message ?? 'Something went wrong. Please try again.',
                },
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    async getAllData() {
        this.getData();
        this.setState({
            getCtrLoader: true,
        });
        this.getCtrAverage.bind(this.setState.bind(this));
        const results = await this.getCtrAverage.update();
        const data = results?.map((x) => ({ id: uuid(), ...x }));

        this.setState({
            getCtrLoader: false,
            averageCTR: Math.round((data?.[0]?.ctr || 0) * 100),
            staticAverageCTR: Math.round((data?.[0]?.ctr || 0) * 100),
        });
    }

    async componentDidMount() {
        this.getAllData();
    }

    render() {
        const { t } = this.props;

        const additionalBox = () => (
            <AdditionalBox
                showLowCTR={this.showLowCTR}
                isShowLowCTR={this.state.isShowLowCTR}
                averageCTR={this.state.averageCTR}
                changeAverageCTR={this.changeAverageCTR}
                t={t}
                staticAverageCTR={this.state.staticAverageCTR}
                getCtrLoader={this.state.getCtrLoader}
            />
        );
        const extraBtns = () => (
            <ExtraButtons
                lastUpdatedDate={this.state.lastUpdatedDate}
                staticAverageCTR={this.state.staticAverageCTR}
                t={t}
            />
        );

        const columns = COLUMNS(this.state.averageCTR / 100, t);
        return (
            <>
                <div className="card table-main rounded-main overflow-hidden">
                    {this.state.isLoading ? (
                        <Loading padding={10} />
                    ) : (
                        <DataTable
                            nodes={this.state.filteredData}
                            additionalBox={additionalBox}
                            sortFns={getSortKeys(columns)}
                            extraBtns={extraBtns}
                            COLUMNS={columns}
                            fileName={t('discover.live_optimization.header')}
                            header={t('discover.live_optimization.header')}
                            customEmptyMessage={t('discover.live_optimization.table.no_data')}
                            forceRun={this.getAllData}
                            additionalStyles={setAdditionalStyles(columns)}
                            searchFunction={this.searchFunction}
                        />
                    )}
                </div>
            </>
        );
    }
}

export default withTranslation()(LiveOptimizationReport);
